import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "@/src/core/api/client";
import {
  boostService,
  FEATURED_HOME_LIMIT,
  FEATURED_PAGE_SIZE,
} from "../services";
import type { ISponsoredListingsResponse, ISponsoredQuery } from "../types";

const boostKeys = {
  all: ["boost"] as const,
  featured: (query: ISponsoredQuery, perPage: number) =>
    [...boostKeys.all, "featured", query, perPage] as const,
  featuredAll: (query: ISponsoredQuery) =>
    [...boostKeys.all, "featured-all", query] as const,
};

/** Raw calls on the Python service. Throw an ApiError on failure. */
export const boostApi = {
  async getSponsoredListings(
    query: ISponsoredQuery,
    page: number,
    perPage: number,
  ): Promise<ISponsoredListingsResponse> {
    const { data } = await api.python.get<ISponsoredListingsResponse>(
      "/public/boosts/listings",
      { params: { ...query, page, per_page: perPage } },
    );
    return {
      ...data,
      data: data.data ?? [],
      pagination: data.pagination ?? null,
    };
  },
};

/** The home row: the first few boosted stays of a tab's campaign. */
export const useFeaturedStaysQuery = (query: ISponsoredQuery | null) =>
  useQuery({
    queryKey: query
      ? boostKeys.featured(query, FEATURED_HOME_LIMIT)
      : [...boostKeys.all, "featured", "none"],
    queryFn: () =>
      boostApi.getSponsoredListings(
        query as ISponsoredQuery,
        1,
        FEATURED_HOME_LIMIT,
      ),
    enabled: query !== null,
  });

/** Every boosted stay of a campaign, a page at a time (the rail's page and drawer). */
export const useFeaturedStaysInfiniteQuery = (
  query: ISponsoredQuery | null,
  initialPage?: ISponsoredListingsResponse,
) =>
  useInfiniteQuery({
    queryKey: query
      ? boostKeys.featuredAll(query)
      : [...boostKeys.all, "featured-all", "none"],
    queryFn: ({ pageParam }) =>
      boostApi.getSponsoredListings(
        query as ISponsoredQuery,
        pageParam,
        FEATURED_PAGE_SIZE,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => boostService.nextPage(last.pagination),
    enabled: query !== null,
    ...(initialPage && {
      initialData: { pages: [initialPage], pageParams: [1] },
    }),
  });
